import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, Package, Search, Truck, Store, Download } from 'lucide-react';
import EscalationQueue from './EscalationQueue';

const STATUS_COLORS = {
  pending:   'bg-amber-900/40 text-amber-200 border-amber-700/50',
  requested: 'bg-amber-900/40 text-amber-200 border-amber-700/50',
  escalated: 'bg-orange-900/40 text-orange-200 border-orange-700/50',
  approved:  'bg-blue-900/40 text-blue-200 border-blue-700/50',
  declined:  'bg-gray-800 text-gray-300 border-gray-600',
  failed:    'bg-red-900/40 text-red-200 border-red-700/50',
  shipped:   'bg-blue-900/40 text-blue-200 border-blue-700/50',
  delivered: 'bg-emerald-900/40 text-emerald-200 border-emerald-700/50',
  completed: 'bg-emerald-900/40 text-emerald-200 border-emerald-700/50',
  cancelled: 'bg-gray-800 text-gray-300 border-gray-600',
  refunded:  'bg-red-900/40 text-red-200 border-red-700/50',
};

const money = (n) => `$${(n || 0).toFixed(2)}`;
const when  = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

function SubOrderRow({ sub, onReset }) {
  const digital = (sub.items || []).filter(i => i.is_digital);
  return (
    <div
      className="flex flex-wrap items-center gap-3 py-2 px-3 rounded-lg bg-white/5 border border-white/10"
      data-testid={`admin-suborder-${sub.order_number}`}
    >
      <Store className="h-4 w-4 text-gray-400 shrink-0" />
      <span className="font-mono text-xs text-gray-300">{sub.order_number}</span>
      <span className="text-sm text-white">{sub.vendor_store_name}</span>
      <Badge className={`text-[10px] border ${STATUS_COLORS[sub.status] || STATUS_COLORS.pending}`}>
        {sub.status}
      </Badge>
      <span className="text-xs text-gray-400">{(sub.items || []).length} item(s)</span>
      <span className="ml-auto text-sm text-gray-200">
        {money(sub.subtotal)} <span className="text-gray-500">· payout {money(sub.vendor_payout)} · fee {money(sub.platform_fee)}</span>
      </span>
      {(sub.tracking || []).length > 0 && (
        <span className="flex items-center gap-1 text-xs text-blue-300 basis-full">
          <Truck className="h-3 w-3" />
          {sub.tracking.map(t => `${t.carrier} ${t.tracking_number}`).join(' · ')}
        </span>
      )}
      {digital.map(item => (
        <span
          key={item.product_id}
          className="flex items-center gap-2 text-xs text-gray-300 basis-full"
          data-testid={`admin-digital-${sub.id}-${item.product_id}`}
        >
          <Download className="h-3 w-3" />
          {item.title}: {item.downloads_used || 0}
          {item.download_limit ? ` of ${item.download_limit}` : ''} download(s) used
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-[11px]"
            onClick={() => onReset?.(sub.id, item.product_id)}
            data-testid={`admin-reset-downloads-${sub.id}-${item.product_id}`}
          >
            Reset
          </Button>
        </span>
      ))}
    </div>
  );
}

export default function AdminMarketplaceOrdersTab() {
  const [view, setView]       = useState('orders');
  const [orders, setOrders]   = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [pages, setPages]     = useState(1);
  const [search, setSearch]   = useState('');
  const [openId, setOpenId]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [returns, setReturns] = useState([]);
  const [notes, setNotes]     = useState({});
  const [busyId, setBusyId]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/marketplace/orders`, {
        params: { page, per_page: 25, ...(search ? { search } : {}) },
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
      setPages(res.data.total_pages || 1);
    } catch (e) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  const loadReturns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/marketplace/returns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReturns(res.data || []);
    } catch (e) {
      setReturns([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadReturns(); }, [loadReturns]);

  const override = async (id, approve) => {
    setBusyId(id);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/marketplace/returns/${id}/override`,
        { approve, note: notes[id] || null },
        { headers: { Authorization: `Bearer ${token}` } });
      await loadReturns();
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not update this return');
    } finally {
      setBusyId(null);
    }
  };

  const resetDownloads = async (subOrderId, productId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/marketplace/orders/receipts/${subOrderId}/reset-downloads/${productId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not reset the download count');
    }
  };

  const openReturns = returns.filter(r => ['requested', 'escalated'].includes(r.status)).length;
  const escalations = returns.filter(r => r.status === 'escalated').length;
  return (
    <div className="space-y-4" data-testid="admin-marketplace-orders-tab">
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={view === 'orders' ? 'default' : 'outline'}
          onClick={() => setView('orders')}
          className={view === 'orders' ? '' : 'border-slate-600 text-slate-200 hover:text-white hover:bg-slate-700/60'}
          data-testid="admin-view-orders"
        >
          Orders
        </Button>
        <Button
          size="sm"
          variant={view === 'returns' ? 'default' : 'outline'}
          onClick={() => setView('returns')}
          className={view === 'returns' ? '' : 'border-slate-600 text-slate-200 hover:text-white hover:bg-slate-700/60'}
          data-testid="admin-view-returns"
        >
          Returns {openReturns > 0 && <Badge className="ml-2 bg-amber-600 text-white text-[10px]">{openReturns}</Badge>}
        </Button>
        <Button
          size="sm"
          variant={view === 'escalations' ? 'default' : 'outline'}
          onClick={() => setView('escalations')}
          className={view === 'escalations' ? '' : 'border-slate-600 text-slate-200 hover:text-white hover:bg-slate-700/60'}
          data-testid="admin-view-escalations"
        >
          Escalations {escalations > 0 && <Badge className="ml-2 bg-orange-600 text-white text-[10px]">{escalations}</Badge>}
        </Button>
      </div>

      {view === 'escalations' ? (
        <EscalationQueue onRuled={() => { loadReturns(); load(); }} />
      ) : view === 'returns' ? (
        <div className="space-y-3" data-testid="admin-returns-list">
          {returns.length === 0 ? (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-10 text-center text-gray-400" data-testid="admin-returns-empty">
                No return requests yet.
              </CardContent>
            </Card>
          ) : (
            returns.map((r) => (
              <Card key={r.id} className="bg-white/5 border-white/10" data-testid={`admin-return-${r.order_number}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-sm text-white">{r.order_number}</span>
                    <Badge className={`text-[10px] border ${STATUS_COLORS[r.status] || STATUS_COLORS.pending}`}>
                      {r.status}
                    </Badge>
                    <span className="text-sm text-gray-300">{r.receipt?.vendor_store_name}</span>
                    <span className="text-xs text-gray-500">{r.receipt?.buyer_name} ({r.receipt?.buyer_email})</span>
                    <span className="ml-auto text-sm text-white">{money(r.amount)}</span>
                  </div>
                  <p className="text-xs text-gray-400">Buyer: {r.reason}</p>
                  {r.vendor_note && <p className="text-xs text-amber-200">Vendor: {r.vendor_note}</p>}
                  {r.buyer_escalation_note && <p className="text-xs text-blue-200">Escalation: {r.buyer_escalation_note}</p>}
                  {r.admin_note && <p className="text-xs text-emerald-200">Admin: {r.admin_note}</p>}
                  {r.execution && (
                    <p className="text-xs text-gray-500">
                      Refunded {money(r.amount)} · off vendor payout {money(r.execution.reversal_amount)} ·
                      {r.execution.stripe_fee_absorbed !== undefined
                        ? `Stripe fee absorbed by platform ${money(r.execution.stripe_fee_absorbed)}`
                        : `vendor Stripe fee ${money(r.execution.vendor_stripe_fee)}`}  · platform fee returned {money(r.execution.platform_fee_returned)}
                      {(r.execution.errors || []).length > 0 && (
                        <span className="text-red-300"> · needs attention: {r.execution.errors.join('; ')}</span>
                      )}
                    </p>
                  )}
                  {r.status !== 'refunded' && (
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Input
                        value={notes[r.id] || ''}
                        onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                        placeholder="Note (shared with buyer and vendor)"
                        className="bg-white/5 border-white/10 text-white max-w-md"
                        data-testid="admin-return-note"
                      />
                      <Button
                        size="sm"
                        disabled={busyId === r.id}
                        onClick={() => override(r.id, true)}
                        data-testid="admin-return-approve"
                      >
                        Force refund
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === r.id}
                        onClick={() => override(r.id, false)}
                        className="border-slate-600 text-slate-200 hover:text-white hover:bg-slate-700/60"
                        data-testid="admin-return-deny"
                      >
                        Uphold decline
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      ) : (
      <>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 flex-1 min-w-[220px]">
          <Search className="h-4 w-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by order number..."
            className="border-0 bg-transparent text-white focus-visible:ring-0"
            data-testid="admin-orders-search"
          />
        </div>
        <span className="text-sm text-gray-400" data-testid="admin-orders-total">{total} payment(s)</span>
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm">Loading orders…</p>
      ) : orders.length === 0 ? (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="py-10 text-center text-gray-400" data-testid="admin-orders-empty">
            <Package className="h-8 w-8 mx-auto mb-3 opacity-40" />
            No marketplace orders yet.
          </CardContent>
        </Card>
      ) : (
        orders.map((o) => {
          const open = openId === o.id;
          return (
            <Card key={o.id} className="bg-white/5 border-white/10" data-testid={`admin-order-${o.id}`}>
              <CardContent className="p-4">
                <button
                  className="w-full flex flex-wrap items-center gap-3 text-left"
                  onClick={() => setOpenId(open ? null : o.id)}
                  data-testid="admin-order-toggle"
                >
                  {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                  <span className="font-mono text-sm text-white">{o.order_number || o.id.slice(0, 8)}</span>
                  <Badge className="text-[10px] border bg-purple-900/40 text-purple-200 border-purple-700/50">{o.status}</Badge>
                  <span className="text-sm text-gray-300">{o.buyer_name || o.customer_id?.slice(0, 8)}</span>
                  <span className="text-xs text-gray-500">{when(o.created_at)}</span>
                  <span className="ml-auto text-sm text-white">
                    {money(o.payment?.amount_total)}{' '}
                    <span className="text-gray-500">· platform {money(o.payment?.amount_platform)}</span>
                  </span>
                  <span className="text-xs text-gray-400 basis-full pl-7">
                    {(o.sub_orders || []).length} vendor receipt(s)
                  </span>
                </button>

                {open && (
                  <div className="mt-3 space-y-2 pl-7">
                    {(o.sub_orders || []).length === 0 ? (
                      <p className="text-xs text-gray-500">
                        No vendor receipts — this payment has not been confirmed yet.
                      </p>
                    ) : (
                      o.sub_orders.map((sub) => <SubOrderRow key={sub.id} sub={sub} onReset={resetDownloads} />)
                    )}
                    {o.shipping_address && (
                      <p className="text-xs text-gray-500">
                        Ship to: {o.shipping_address.name}, {o.shipping_address.line1}, {o.shipping_address.city},{' '}
                        {o.shipping_address.state} {o.shipping_address.zip}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="admin-orders-prev">
            Previous
          </Button>
          <span className="text-sm text-gray-400">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="admin-orders-next">
            Next
          </Button>
        </div>
      )}
      </>
      )}
    </div>
  );
}
